/**
 * ValuesComparison - Tableau comparatif compact des 4 sources
 * Remplace SourceTabs pour une vue d'ensemble rapide
 * Avec accès aux détails via SourceDetailPanel
 *
 * V2: Ajout logos officiels des sources
 */

import { SourceINRS, SourceANSES, SourceECHA, SourceAGRITOX } from '../../services/api'
import SourceBadge from '../sources/SourceBadge'

interface ValuesComparisonProps {
  sources: {
    inrs: SourceINRS
    anses: SourceANSES
    echa: SourceECHA
    agritox: SourceAGRITOX
  }
  onSourceClick: (source: 'inrs' | 'anses' | 'echa' | 'agritox') => void
}

export default function ValuesComparison({ sources, onSourceClick }: ValuesComparisonProps) {
  const { inrs, anses, echa, agritox } = sources

  // Formater une valeur avec unité ou afficher N/A
  const formatValue = (value: { valeur: number; unite: string } | null | undefined): string => {
    if (!value) return '—'
    return `${value.valeur} ${value.unite}`
  }

  // Données du tableau
  const sourceData = [
    {
      key: 'inrs' as const,
      available: inrs.disponible,
      mainValue: formatValue(inrs.vlep_8h),
      mainLabel: 'VLEP-8h',
      secondaryValue: formatValue(inrs.vlep_court_terme),
      secondaryLabel: 'VLEP CT',
      date: inrs.date_donnees
    },
    {
      key: 'anses' as const,
      available: anses.disponible,
      mainValue: formatValue(anses.vtr_inhalation),
      mainLabel: 'VTR inh.',
      secondaryValue: formatValue(anses.vtr_orale),
      secondaryLabel: 'VTR orale',
      date: anses.date_donnees
    },
    {
      key: 'echa' as const,
      available: echa.disponible,
      mainValue: echa.svhc ? 'Oui' : 'Non',
      mainLabel: 'SVHC',
      secondaryValue: echa.raison_inclusion ? 'Voir détails' : '—',
      secondaryLabel: 'Raison',
      date: echa.date_donnees
    },
    {
      key: 'agritox' as const,
      available: agritox.disponible,
      mainValue: formatValue(agritox.dja),
      mainLabel: 'DJA',
      secondaryValue: formatValue(agritox.aoel),
      secondaryLabel: 'AOEL',
      date: agritox.date_donnees
    }
  ]

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">Valeurs de référence</h3>
        <p className="text-xs text-gray-500 mt-0.5 print:hidden">Cliquez sur une source pour voir les détails</p>
      </div>

      {/* Version mobile: cards empilées (masquée à l'impression) */}
      <div className="md:hidden print:hidden divide-y divide-gray-100">
        {sourceData.map((source) => (
          <button
            key={source.key}
            onClick={() => onSourceClick(source.key)}
            className="w-full p-4 text-left hover:bg-gray-50 transition-colors"
            disabled={!source.available}
          >
            <div className="flex items-center justify-between mb-2">
              <div className={source.available ? '' : 'opacity-50 grayscale'}>
                <SourceBadge
                  sourceKey={source.key}
                  size="sm"
                  showLink={false}
                />
              </div>
              {source.available && (
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </div>
            {source.available ? (
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">{source.mainLabel}:</span>
                  <span className="ml-1 font-medium text-gray-900">{source.mainValue}</span>
                </div>
                <div>
                  <span className="text-gray-500">{source.secondaryLabel}:</span>
                  <span className="ml-1 font-medium text-gray-900">{source.secondaryValue}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Non disponible</p>
            )}
          </button>
        ))}
      </div>

      {/* Version desktop: tableau (toujours visible à l'impression) */}
      <div className="hidden md:block print:block overflow-x-auto print:overflow-visible">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valeur principale
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valeur secondaire
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date données
              </th>
              <th className="px-4 py-2 print:hidden"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sourceData.map((source) => (
              <tr
                key={source.key}
                className={source.available ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50'}
                onClick={() => source.available && onSourceClick(source.key)}
              >
                <td className="px-4 py-3">
                  <div className={source.available ? '' : 'grayscale'}>
                    <SourceBadge
                      sourceKey={source.key}
                      size="md"
                      showLink={false}
                    />
                  </div>
                </td>
                <td className="px-4 py-3">
                  {source.available ? (
                    <div className="text-sm">
                      <span className="text-gray-500">{source.mainLabel}:</span>
                      <span className="ml-1 font-medium text-gray-900">{source.mainValue}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {source.available ? (
                    <div className="text-sm">
                      <span className="text-gray-500">{source.secondaryLabel}:</span>
                      <span className="ml-1 font-medium text-gray-900">{source.secondaryValue}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">
                  {source.date || 'Date non fournie'}
                </td>
                <td className="px-4 py-3 print:hidden">
                  {source.available && (
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
